/**
 * @fileoverview Left-nav menu item definitions.
 * @description The single list NavMenu renders, filtered down to whichever items the signed-in
 * user's privileges (from GET /identity/me) allow — the one place a new dashboard-area page
 * registers itself in the sidebar, and the privilege it needs to appear there. Sales items are in
 * the InsightBox Sales design's sidebar order; banking items follow, in the InsightBox Banking
 * design's sidebar order (Audit Log, then Capabilities, Super Admin only, closing the Sales group). Each banking privilege key is shared by every sub-page under the same
 * top-level group (e.g. the two Membership & Share Capital sub-pages both need
 * `banking:membership:read`), so only the group's own hub/leaf entry appears here — sub-pages are
 * reached by navigating from their hub, not from this top-level list. "Dashboard" (Sales) and "Bank
 * Details" (leading the Banking group, ahead of "Bank Overview") both use `dividerAfter`, setting
 * each apart from the rest of its own module's pages below it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { ROUTES } from '@/shared/constants/routes.constants'

export type NavIconKey =
  | 'dashboard'
  | 'users'
  | 'myPage'
  | 'pipeline'
  | 'team'
  | 'performance'
  | 'clients'
  | 'capabilities'
  | 'auditLog'
  | 'overview'
  | 'bankDetails'
  | 'membership'
  | 'customerBase'
  | 'deposits'
  | 'loans'
  | 'investments'
  | 'assetQuality'
  | 'profitability'
  | 'capitalAdequacy'
  | 'branchAnalytics'
  | 'productivity'
  | 'businessPerformance'

export interface NavItem {
  labelKey: string
  path: string
  privilege: string
  icon: NavIconKey
  /** Renders a divider line directly below this item, e.g. to set "Dashboard"/"Bank Details" apart
   * at the top of their own group. Omitted everywhere else. */
  dividerAfter?: boolean
}

export const NAV_ITEMS: NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    path: ROUTES.DASHBOARD,
    privilege: 'sales:dashboard:view',
    icon: 'dashboard',
    dividerAfter: true,
  },
  {
    labelKey: 'nav.userManagement',
    path: ROUTES.SALES_USERS,
    privilege: 'sales:users:manage',
    icon: 'users',
  },
  {
    labelKey: 'nav.myPage',
    path: ROUTES.SALES_MY_PAGE,
    privilege: 'sales:my-page:view',
    icon: 'myPage',
  },
  {
    labelKey: 'nav.salesPipeline',
    path: ROUTES.SALES_PIPELINE,
    privilege: 'sales:pipeline:read',
    icon: 'pipeline',
  },
  {
    labelKey: 'nav.salesTeam',
    path: ROUTES.SALES_TEAM,
    privilege: 'sales:team:read',
    icon: 'team',
  },
  {
    labelKey: 'nav.salesPerformance',
    path: ROUTES.SALES_PERFORMANCE,
    privilege: 'sales:performance:read',
    icon: 'performance',
  },
  {
    labelKey: 'nav.activeClients',
    path: ROUTES.SALES_CLIENTS,
    privilege: 'sales:clients:read',
    icon: 'clients',
  },
  {
    labelKey: 'nav.auditLog',
    path: ROUTES.AUDIT_LOG,
    privilege: 'audit:logs:read',
    icon: 'auditLog',
  },
  {
    labelKey: 'nav.capabilities',
    path: ROUTES.SALES_CAPABILITIES,
    privilege: 'capabilities:manage',
    icon: 'capabilities',
  },
  {
    labelKey: 'nav.bankDetails',
    path: ROUTES.BANKING_BANK_DETAILS,
    privilege: 'banking:bank-details:view',
    icon: 'bankDetails',
    dividerAfter: true,
  },
  {
    labelKey: 'nav.bankOverview',
    path: ROUTES.BANKING_OVERVIEW,
    privilege: 'banking:overview:view',
    icon: 'overview',
  },
  {
    labelKey: 'nav.membershipShareCapital',
    path: ROUTES.BANKING_MEMBERSHIP,
    privilege: 'banking:membership:read',
    icon: 'membership',
  },
  {
    labelKey: 'nav.customerBase',
    path: ROUTES.BANKING_CUSTOMER_BASE,
    privilege: 'banking:customer-base:read',
    icon: 'customerBase',
  },
  {
    labelKey: 'nav.depositsPortfolio',
    path: ROUTES.BANKING_DEPOSITS,
    privilege: 'banking:deposits:read',
    icon: 'deposits',
  },
  {
    labelKey: 'nav.loansAdvances',
    path: ROUTES.BANKING_LOANS,
    privilege: 'banking:loans:read',
    icon: 'loans',
  },
  {
    labelKey: 'nav.investments',
    path: ROUTES.BANKING_INVESTMENTS,
    privilege: 'banking:investments:read',
    icon: 'investments',
  },
  {
    labelKey: 'nav.assetQuality',
    path: ROUTES.BANKING_ASSET_QUALITY,
    privilege: 'banking:asset-quality:read',
    icon: 'assetQuality',
  },
  {
    labelKey: 'nav.profitability',
    path: ROUTES.BANKING_PROFITABILITY,
    privilege: 'banking:profitability:read',
    icon: 'profitability',
  },
  {
    labelKey: 'nav.capitalAdequacy',
    path: ROUTES.BANKING_CAPITAL_ADEQUACY,
    privilege: 'banking:capital-adequacy:read',
    icon: 'capitalAdequacy',
  },
  {
    labelKey: 'nav.branchAnalytics',
    path: ROUTES.BANKING_BRANCH_ANALYTICS,
    privilege: 'banking:branch-analytics:read',
    icon: 'branchAnalytics',
  },
  {
    labelKey: 'nav.productivityEfficiency',
    path: ROUTES.BANKING_PRODUCTIVITY,
    privilege: 'banking:productivity:read',
    icon: 'productivity',
  },
  {
    labelKey: 'nav.businessPerformance',
    path: ROUTES.BANKING_BUSINESS_PERFORMANCE,
    privilege: 'banking:business-performance:read',
    icon: 'businessPerformance',
  },
]
